import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { FormBase } from 'src/app/_models/form-base';
import { MatFormFieldComponent } from '../../_components/forms/mat-form-field/mat-form-field.component';
import { Checkbox } from 'src/app/_models/checkbox';
import { CheckboxComponent } from '../../_components/selection-controls/checkbox/checkbox.component';
import { ErrorObject } from 'src/app/_models/error';
import {
  handleDigitFocus,
  handleDigitInput,
  handleDigitKeydown,
  handleDigitPaste,
} from 'src/app/_helpers/utils';

@Component({
  selector: 'app-2fa',
  templateUrl: './2fa.component.html',
  styleUrl: './2fa.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgOptimizedImage,
    ReactiveFormsModule,
    ButtonComponent,
    MatFormFieldComponent,
    CheckboxComponent,
  ],
})
export class TwoFaLoginChallengeComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isProcessing = signal(false);
  readonly error = signal<ErrorObject | null>(null);
  isRecoveryCodeMode = signal(false);
  // 'loginRememberMe' is a one-shot value written to sessionStorage by the login component
  // immediately before navigating here, so the user's "remember me" choice carries through
  // the 2FA challenge. It is consumed and deleted here on construction.
  readonly rememberMe = signal(
    sessionStorage.getItem('loginRememberMe') === 'true',
  );

  constructor() {
    sessionStorage.removeItem('loginRememberMe');
  }

  readonly digitInputs =
    viewChildren<ElementRef<HTMLInputElement>>('digitInput');

  readonly rememberDevice = new Checkbox(
    'rememberDevice',
    'Remember this device for 14 days',
    'done',
  );

  readonly recoveryCodeField = new FormBase({
    key: 'recoveryCode',
    label: 'Recovery Code',
    type: 'text',
    placeholder: 'Enter your recovery code',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  readonly challengeForm = this.formBuilder.group({
    digit0: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit1: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit2: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit3: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit4: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit5: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    [this.rememberDevice.id]: [false],
  });

  readonly recoveryCodeForm = this.formBuilder.group({
    recoveryCode: [
      this.recoveryCodeField.value,
      // Recovery codes are generated as 11-character strings by the API. Update both validators if the backend format changes.
      [Validators.required, Validators.minLength(11), Validators.maxLength(11)],
    ],
  });

  private get digitControls() {
    const { digit0, digit1, digit2, digit3, digit4, digit5 } =
      this.challengeForm.controls;
    return [digit0, digit1, digit2, digit3, digit4, digit5];
  }

  onDigitFocus(event: FocusEvent): void {
    handleDigitFocus(event);
  }

  onDigitInput(index: number, event: Event): void {
    handleDigitInput(index, event, this.digitInputs());
  }

  onDigitKeydown(event: KeyboardEvent, index: number): void {
    handleDigitKeydown(event, index, this.digitInputs(), this.digitControls);
  }

  onPaste(event: ClipboardEvent): void {
    handleDigitPaste(event, this.digitInputs(), this.digitControls);
  }

  handleSubmit(): void {
    if (this.isProcessing()) return;

    this.isProcessing.set(true);
    this.error.set(null);

    const isRecoveryCode = this.isRecoveryCodeMode();
    const rememberDevice = !!this.challengeForm.value[this.rememberDevice.id];
    const code = isRecoveryCode
      ? (this.recoveryCodeForm.value.recoveryCode ?? '')
      : this.digitControls.map((c) => c.value).join('');

    if (!isRecoveryCode) {
      this.authService
        .verifyTwoFactorCode({ code, rememberMe: this.rememberMe(), rememberDevice })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.isProcessing.set(false);
            void this.router.navigate(['/dashboard/devices']);
          },
          error: (error: ErrorObject) => {
            this.isProcessing.set(false);
            this.error.set(error);
          },
          complete: () => this.isProcessing.set(false),
        });
    } else {
      this.authService
        .verifyTwoFactorRecoveryCode({ code })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.isProcessing.set(false);
            void this.router.navigate(['/dashboard/devices']);
          },
          error: (error: ErrorObject) => {
            this.isProcessing.set(false);
            this.error.set(error);
          },
          complete: () => this.isProcessing.set(false),
        });
    }
  }

  useRecoveryCode(): void {
    this.error.set(null);
    this.isRecoveryCodeMode.set(true);
  }

  use2FACode(): void {
    this.error.set(null);
    this.isRecoveryCodeMode.set(false);
  }
}
